import { useEffect, useState } from "react"

import { Button, DatePicker, Dropdown, Switch, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import {
  selectBoardsSharingDataExpiresSelector,
  selectBoardsSharingSettingsEnabledSelector,
  selectBoardsSharingToggleSettingsSelector,
  selectCollaborators,
} from "features/common/model/selectors/sharingSelectors"
import { patchExpirationDateSharingSettingsThunk, patchSharingSettingsThunk } from "features/common/model/slice/sharingSlice"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { CalendarOutlined, DownOutlined } from "shared/ui"
import { createGlobalStyle, styled } from "styled-components"

import { CollaboratorsInput } from "./CollaboratorsInput"
import { CollaboratorsList } from "./CollaboratorsList"

const { Text } = Typography

export const DatePickerPopupStyle = createGlobalStyle`
  #share-link-lifetime-selector .ant-picker-dropdown {
    padding-left: 14px;
  }

  .ant-picker-now-btn {
    color: var(--color-primary) !important;
  }

  .ant-tooltip {
    width: fit-content;
  }
`

export const SettingsTab = ({ itemType, id, onPendingCollaboratorChange }: { itemType: string, id: string, onPendingCollaboratorChange: (value: string) => void }) => {
  const now = dayjs()
  const day = now.add(1, 'day')
  const week = now.add(1, 'week')
  const month = now.add(1, 'month')
  const dispatch = useAppDispatch()

  const sharingSettingsEnabled = useAppSelector(selectBoardsSharingSettingsEnabledSelector)
  const toggleSharingSettings = useAppSelector(selectBoardsSharingToggleSettingsSelector)
  const expiresDate = useAppSelector(selectBoardsSharingDataExpiresSelector)
  const collaborators = useAppSelector(selectCollaborators)

  const formatTill = (date: dayjs.Dayjs) => `Till ${date.format('MMM D, HH:mm')}`

  const [selectedLifetime, setSelectedLifetime] = useState<string | null>(null)
  const [selectedEndDate, setSelectedEndDate] = useState<dayjs.Dayjs | null>(null)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)

  const getTimeByLifetime = (lifetime: string) => {
    switch (lifetime) {
    case 'day':
      return day.format()
    case 'week':
      return week.format()
    case 'month':
      return month.format()
    case 'custom':
      return selectedEndDate?.format()
    default:
      return ''
    }
  }

  useEffect(() => {
    setSelectedLifetime(expiresDate ? 'custom' : 'always')
    setSelectedEndDate(expiresDate ? dayjs(expiresDate) : null)
  }, [expiresDate])

  const handleLifetimeChange = (lifetime: string, date?: dayjs.Dayjs) => {
    dispatch(patchExpirationDateSharingSettingsThunk({ nodeId: id, time: date ? date.format() : getTimeByLifetime(lifetime) }))
  }

  const handlePermissionChange = (permissionId: string) => (checked: boolean) => {
    dispatch(patchSharingSettingsThunk({ nodeId: id, permission: { id: permissionId, enabled: checked } }))
  }

  const getPermissionValue = (permissionId: string) => {
    const permission = toggleSharingSettings.find(p => p.id === permissionId)
    return permission?.enabled || false
  }

  const checkEnabledPermission = (permissionId: string) => {
    const permission = sharingSettingsEnabled.find(p => p.id === permissionId)
    return permission?.enabled || false
  }

  const handleCustomClick = () => {
    setSelectedLifetime('custom')
    setIsCalendarOpen(true)
  }

  const disabledDate = (current: dayjs.Dayjs) => {
    return current && current < now.startOf('day')
  }

  const disabledTime = (date: dayjs.Dayjs | null) => {
    if (date && date.isSame(now, 'day')) {
      return {
        disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
        disabledMinutes: (selectedHour: number) => selectedHour === now.hour() ? Array.from({ length: now.minute() }, (_, i) => i) : [],
        disabledSeconds: () => [],
      }
    }
    return {}
  }

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    setSelectedEndDate(date)
  }

  const handleCalendarOk = (date: dayjs.Dayjs) => {
    handleLifetimeChange('custom', date)
    setIsCalendarOpen(false)
  }

  const lifetimeOptions = [
    {
      label: 'Always',
      key: 'always',
    },
    {
      label: (
        <LifetimeMenuItem>
          <span>
Day
          </span>

          <LifetimeMenuDate>
            {formatTill(day)}
          </LifetimeMenuDate>
        </LifetimeMenuItem>
      ),
      key: 'day',
    },
    {
      label: (
        <LifetimeMenuItem>
          <span>
Week
          </span>

          <LifetimeMenuDate>
            {formatTill(week)}
          </LifetimeMenuDate>
        </LifetimeMenuItem>
      ),
      key: 'week',
    },
    {
      label: (
        <LifetimeMenuItem>
          <span>
Month
          </span>

          <LifetimeMenuDate>
            {formatTill(month)}
          </LifetimeMenuDate>
        </LifetimeMenuItem>
      ),
      key: 'month',
    },
    {
      type: 'divider' as const,
    },
    {
      label: (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto', color: '#000', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <CalendarOutlined />
            Select date and time
        </Button>
      ),
      key: 'custom',
      onClick: handleCustomClick,
    },
  ]

  const getSelectedLabel = () => {
    const found = lifetimeOptions.find(opt => opt.key === selectedLifetime)
    if (!found) return 'Always'
    if (selectedLifetime === 'custom' && selectedEndDate) {
      return (
        <>
          <span>
            {now.format('MMM D, HH:mm')}

            {' '}
            —

            {' '}

            {selectedEndDate.format('MMM D, HH:mm')}
          </span>
        </>
      )
    }
    if (typeof found.label === 'string') return found.label
    switch (found.key) {
    case 'day':
      return (
        <>
          <span>
Day
          </span>

          <span style={{ color: '#aaa', marginLeft: 8 }}>
            {formatTill(day)}
          </span>
        </>
      )
    case 'week':
      return (
        <>
          <span>
Week
          </span>

          <span style={{ color: '#aaa', marginLeft: 8 }}>
            {formatTill(week)}
          </span>
        </>
      )
    case 'month':
      return (
        <>
          <span>
Month
          </span>

          <span style={{ color: '#aaa', marginLeft: 8 }}>
            {formatTill(month)}
          </span>
        </>
      )
    default:
      return found.label
    }
  }

  const handleMenuClick = (info: any) => {
    if (info.key === 'custom') return
    setSelectedLifetime(info.key)
    handleLifetimeChange(info.key)
  }

  return (
    <>
      <DatePickerPopupStyle />

      <CollaboratorsInput onValueChange={onPendingCollaboratorChange} />

      <CollaboratorsList collaborators={collaborators} />

      <ShareLinkPermissionList>
        {checkEnabledPermission('download_fs_nodes') && (
          <ShareLinkPermissionItem>
            <Tooltip
              title={"Allows downloading assets from the shared content"}
              placement="topLeft"
            >
              <SettingLabel>
              Download assets
              </SettingLabel>
            </Tooltip>

            <Switch
              size="small"
              checked={getPermissionValue('download_fs_nodes')}
              onChange={handlePermissionChange('download_fs_nodes')}
            />
          </ShareLinkPermissionItem>
        )}

        {itemType === 'dir' && checkEnabledPermission('upload_fs_nodes') && (
          <ShareLinkPermissionItem>
            <Tooltip title="Allows adding new assets to the shared content"
              placement="topLeft"
            >
              <SettingLabel>
                Upload new assets
              </SettingLabel>
            </Tooltip>

            <Switch
              size="small"
              checked={getPermissionValue('upload_fs_nodes')}
              onChange={handlePermissionChange('upload_fs_nodes')}
            />
          </ShareLinkPermissionItem>
        )}

        {checkEnabledPermission('create_comments') && (
          <ShareLinkPermissionItem>
            <Tooltip title="Allows leaving feedback or notes on the content"
              placement="topLeft"
            >
              <SettingLabel>
            Leave comments
              </SettingLabel>
            </Tooltip>

            <Switch
              size="small"
              checked={getPermissionValue('create_comments')}
              onChange={handlePermissionChange('create_comments')}
            />
          </ShareLinkPermissionItem>
        )}

        {checkEnabledPermission('edit_fs_nodes') && (
          <ShareLinkPermissionItem>
            <Tooltip title="Allows making changes to the shared content"
              placement="topLeft"
            >
              <SettingLabel>
              Edit content
              </SettingLabel>
            </Tooltip>

            <Switch
              size="small"
              checked={getPermissionValue('edit_fs_nodes')}
              onChange={handlePermissionChange('edit_fs_nodes')}
            />
          </ShareLinkPermissionItem>
        )}

        {checkEnabledPermission('get_fs_node_versions') && (
          <ShareLinkPermissionItem>
            <Tooltip title="Allows viewing past changes to the assets"
              placement="topLeft"
            >
              <SettingLabel>
            View version history
              </SettingLabel>
            </Tooltip>

            <Switch
              size="small"
              checked={getPermissionValue('get_fs_node_versions')}
              onChange={handlePermissionChange('get_fs_node_versions')}
            />
          </ShareLinkPermissionItem>
        )}

        {/* <ShareLinkPermissionItem> Пока что не реализовано
          <SettingLabel>
            Inform me about new comments
          </SettingLabel>

          <Switch size="small" />
        </ShareLinkPermissionItem> */}

      </ShareLinkPermissionList>

      <ShareLinkLifetimeSelector id="share-link-lifetime-selector">
        <Text>
            Link lifetime
        </Text>

        <Dropdown
          trigger={["click"]}
          menu={{
            items: lifetimeOptions,
            onClick: handleMenuClick,
          }}
          dropdownRender={menu => (
            <ShareLinkLifetimeMenu>
              {menu}
            </ShareLinkLifetimeMenu>
          )}
        >
          <SText style={{ cursor: 'pointer' }}>
            {getSelectedLabel()}

            <DownOutlined />
          </SText>
        </Dropdown>
      </ShareLinkLifetimeSelector>

      {selectedLifetime === 'custom' && (
        <DatePicker
          showTime
          format="YYYY-MM-DD HH:mm"
          value={selectedEndDate}
          onChange={handleDateChange}
          disabledDate={disabledDate}
          disabledTime={disabledTime}
          placeholder="Select end date and time"
          style={{
            color: 'var(--color-primary)',
            width: 0,
            height: 0,
            margin: 0,
            padding: 0,
            border: 'none',
            opacity: 0,
            position: 'absolute',
            pointerEvents: 'none'
          }}
          open={isCalendarOpen}
          onOpenChange={setIsCalendarOpen}
          onOk={handleCalendarOk}
          allowClear={false}
          inputReadOnly
          getPopupContainer={() => document.getElementById('share-link-lifetime-selector')}
        />
      )}
    </>
  )
}

const ShareLinkLifetimeMenu = styled.div`
  min-width: 260px;
`

const LifetimeMenuItem = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const LifetimeMenuDate = styled.span`
  color: #aaa;
  margin-left: 0;
  margin-top: 2px;
  font-size: 13px;
  padding-left: 24px;
  min-height: 18px;
`

const ShareLinkLifetimeSelector = styled.div`  
  background-color: #0000000A;
  padding: 12px 20px;
  display: flex;
  gap: 4px;
  border-radius: 4px;
  align-items: center;
  justify-content: space-between;
`
const SText = styled(Text)`
  display: flex;
  align-items: center;
  gap: 8px;
`
const SettingLabel = styled(Text)`
  font-size: 14px;
  font-weight: 500;
`

const ShareLinkPermissionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 0;
`

const ShareLinkPermissionList = styled.div`
  background-color: #0000000A;
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-radius: 4px;
`
